import { RootState } from "common/reducers";
import { ids } from "common/selectors/idsManagement.selectors";
import { List } from "immutable";
import { createSelector } from "reselect";
import { Nullable } from "common/types";
import { ProductRecord } from "common/records/Product";
import { DocumentRecord } from "common/records/Document";

const model = "documents";

/**
 * Loads byId key from documents
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, DocumentRecord>} Map<number, DocumentRecord>
 */
export const byId = (state: RootState, _props?: any) => state.getIn([model, "byId"]);

/**
 * Loads the document using id
 *
 * @param {RootState} state The RootState
 * @param props
 * @returns DocumentRecord, or undefined when no document is loaded for the id
 */
export const document = (state: RootState, props: { documentId: number }): Nullable<DocumentRecord> =>
  byId(state).get(props.documentId);

/**
 * Loads the list of price list ids
 *
 * @param {RootState} state The RootState
 * @returns {List<IdType>} List of ids
 */
export const documentIds = (state: RootState) => ids(state, { modelName: model }) as List<number>;

/**
 * Evaluates if all documents have the given `displayProp` set to true
 *
 * @param {RootState} state The RootState
 * @returns {boolean} The boolean value
 */
export const documentsDisplayChecked = createSelector(
  [documentIds, byId, (state, props) => props],
  (ids, byId, props) => {
    return ids.every((id) => byId.get(id)?.get(props.displayProp) === true);
  },
);

/**
 * Document URLs that travel onto a line item from the selected product and ALL its ancestors,
 * filtered by display. Ports rsf-app's `cachedAncestorDocuments`: walks the ancestor chain (self +
 * parents) via `parent_id` through `productOverviewById`, keeps documents whose `display` is
 * `"both"` or matches, and dedupes by `md5_sum`.
 *
 * @param {RootState} state The RootState
 * @param {{ productId: Nullable<number>, display: string }} props
 * @returns {List<string>} the de-duplicated document URLs
 */
export const productAncestorDocumentsInSnapshot = (
  state: RootState,
  props: { productId: Nullable<number>; display: string },
): List<string> => {
  const { productId, display } = props;
  if (!productId) return List<string>();

  const seenMd5 = new Set<string>();
  let urls = List<string>();

  let currentId: Nullable<number> = productId;
  // Cap the ancestor walk to guard against a malformed parent_id cycle (matches the breadcrumb
  // walk's depth guard); a real product hierarchy is far shallower than this.
  let guard = 0;
  while (currentId && guard < 50) {
    guard += 1;
    // Flat store read (not productOverviewById) — product.selectors imports from this module, so
    // importing it back would form a bidirectional cycle. Matches the roomsForGroup pattern.
    const product = state.getIn(["products", "productOverviewsById", currentId]) as Nullable<ProductRecord>;
    if (!product) break;

    product.document_ids.forEach((docId: number) => {
      const doc = byId(state).get(docId);
      if (!doc) return;
      const matches = doc.display === "both" || doc.display === display || display === "both";
      if (!matches || !doc.url) return;
      // De-dupe by md5_sum so the same document inherited at multiple levels appears once;
      // documents without an md5_sum are always kept (keyed by url as a fallback).
      const key = doc.md5_sum ?? doc.url;
      if (seenMd5.has(key)) return;
      seenMd5.add(key);
      urls = urls.push(doc.url);
    });

    currentId = product.parent_id;
  }

  return urls;
};

export const relativePathProductAncestorDocumentsInSnapshot = (
  state: RootState,
  props: { productId: Nullable<number>; display: string },
): List<string> => productAncestorDocumentsInSnapshot(state, props).map((url) => new URL(url).pathname);
